/**
 * Swiper Engine — Horizontal Scroll
 *
 * Inicjalizuje Swiper.js na sekcji `.horizontal` (front-page).
 * Odpala się po evencie `starter:finished`.
 * Tylko desktop (min-width: 1024px).
 *
 * Scroll lock: wheel listener na window z capture:true — przechwytuje
 * eventy PRZED Lenis (który jest w fazie bubble). Lock aktywny tylko gdy
 * .horizontal jest przy szczycie viewportu.
 *
 * Panel Offer: zamiast nested Swipera używa prostego cyclera — kółko myszy
 * przechodzi przez elementy oferty (is-active). Na krawędziach (pierwszy/ostatni)
 * event przekazany do outer Swipera.
 */
function initHorizontalSwiper() {
  if (!window.matchMedia('(min-width: 1024px)').matches) return;

  const container = document.querySelector('.horizontal');
  if (!container) return;

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: false }));
  }

  /* =========================
     OFFER CYCLER
     Przełącza is-active na tytułach, opisach i filmach oferty.
     Zastępuje nested Swiper na desktop.
  ========================= */
  let offerCycler = null;

  function setupOfferCycler() {
    const offerPanel = container.querySelector('.panel--offer');
    if (!offerPanel) return null;

    const titles = Array.from(offerPanel.querySelectorAll('.offer__title'));
    const descs  = Array.from(offerPanel.querySelectorAll('.offer__desc'));
    const videos = Array.from(offerPanel.querySelectorAll('.offer__video'));

    if (!titles.length) return null;

    let idx = 0;

    function setActive(i) {
      idx = Math.max(0, Math.min(i, titles.length - 1));

      titles.forEach((el, n) => el.classList.toggle('is-active', n === idx));
      descs.forEach((el, n)  => el.classList.toggle('is-active', n === idx));
      videos.forEach((el, n) => {
        const active = n === idx;
        el.classList.toggle('is-active', active);
        el.classList.toggle('is-shrunk', active);
        if (active) {
          // Lazy load
          const src = el.querySelector('source[data-src]');
          if (src && !src.src) { src.src = src.dataset.src; el.load(); }
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      });
    }

    // Inicjalizacja — pierwszy element aktywny
    setActive(0);

    // Klik na tytuł → aktywuje ten element
    titles.forEach((title, n) => {
      title.addEventListener('click', () => setActive(n));
    });

    return {
      get isBeginning() { return idx === 0; },
      get isEnd()       { return idx === titles.length - 1; },
      next()   { setActive(idx + 1); },
      prev()   { setActive(idx - 1); },
      reset()  { setActive(0); },
      toLast() { setActive(titles.length - 1); },
    };
  }

  /* =========================
     TRUST SCROLLER
     Scroll-driven marquee logos (laptop+). Wheel delta → translateX na
     .trust__logos. Outer Swiper przechodzi do następnego/poprzedniego slajdu
     dopiero gdy progress osiągnie krawędź.
  ========================= */
  let trustScroller = null;

  function setupTrustScroller() {
    const trustPanel = container.querySelector('.panel--trust');
    if (!trustPanel) return null;
    const logosContainer = trustPanel.querySelector('.trust__logos');
    if (!logosContainer) return null;
    const logos = Array.from(logosContainer.querySelectorAll('.trust__logo'));
    if (!logos.length) return null;
    const trustTitle = trustPanel.querySelector('.trust__title');

    // Geometria scrolla (cache'owana — odczyty offset*/scrollWidth forsują
    // layout reflow, więc robimy je RAZ w measure(), nie per klatka).
    let startX = 0;
    let endX = 0;
    let travel = 1;
    let progress = 0;        // aktualna pozycja (lerpowana per rAF)
    let targetProgress = 0;  // docelowa pozycja (ustawiana wheel eventem)
    let containerAbsLeft = 0;
    let triggerX = 0;
    let straightenEndX = 0;
    let logoCache = [];
    let rafId = 0;
    const triggerOffset = 1.0;
    const straightenEndOffset = 0.78;
    // Współczynnik lerp per klatka — 0.15 daje ~150ms dojazdu (60fps).
    // Niższe = bardziej smooth ale więcej "ciągnięcia", wyższe = ostrzej.
    const LERP = 0.15;
    const EPSILON = 0.0005;

    function getAbsoluteLeft(el) {
      let x = 0;
      while (el && el !== trustPanel) {
        x += el.offsetLeft;
        el = el.offsetParent;
      }
      return x;
    }

    function measure() {
      const viewportW = trustPanel.clientWidth || window.innerWidth;
      const containerW = logosContainer.scrollWidth;
      containerAbsLeft = getAbsoluteLeft(logosContainer);

      const titleAbsLeft = trustTitle ? getAbsoluteLeft(trustTitle) : 0;
      const titleW = trustTitle ? trustTitle.offsetWidth : 0;
      const titleRightPx = titleAbsLeft + titleW;

      startX = viewportW - containerAbsLeft;
      endX = titleRightPx - containerAbsLeft - containerW;
      travel = Math.max(1, startX - endX);

      triggerX = viewportW * triggerOffset;
      straightenEndX = viewportW * straightenEndOffset;

      // Cache per-logo offsetLeft/Width — zero reflow per klatka.
      logoCache = logos.map((logo, idx) => ({
        el: logo,
        offsetLeft: logo.offsetLeft,
        offsetWidth: logo.offsetWidth,
        yOff: idx % 2 === 0 ? -175 : 175,
        rot: idx % 2 === 0 ? -23 : 23,
        lastT: -1,
      }));
    }

    function setInitialLogos() {
      for (let i = 0; i < logoCache.length; i++) {
        const c = logoCache[i];
        if (typeof gsap !== 'undefined') {
          gsap.set(c.el, { y: c.yOff, rotation: c.rot, opacity: 0.15, force3D: true });
        }
        c.lastT = 0;
      }
    }

    function updateLogos(currentX) {
      const denom = (triggerX - straightenEndX) || 1;
      for (let i = 0; i < logoCache.length; i++) {
        const c = logoCache[i];
        const logoRight = containerAbsLeft + currentX + c.offsetLeft + c.offsetWidth;
        let t = (triggerX - logoRight) / denom;
        if (t < 0) t = 0; else if (t > 1) t = 1;

        // Pomijaj redundantne sety (zmiana < 0.5%) — tnie 80%+ wywołań gsap.set.
        if (Math.abs(t - c.lastT) < 0.005) continue;
        c.lastT = t;

        const inv = 1 - t;
        if (typeof gsap !== 'undefined') {
          gsap.set(c.el, {
            y: c.yOff * inv,
            rotation: c.rot * inv,
            opacity: 0.15 + 0.85 * t,
            force3D: true,
          });
        }
      }
    }

    function render() {
      const currentX = startX - progress * travel;
      if (typeof gsap !== 'undefined') {
        gsap.set(logosContainer, { x: currentX, force3D: true });
      } else {
        logosContainer.style.transform = 'translate3d(' + currentX + 'px,0,0)';
      }
      updateLogos(currentX);
    }

    function setProgressInstant(p) {
      progress = targetProgress = Math.max(0, Math.min(1, p));
      render();
    }

    function tick() {
      const diff = targetProgress - progress;
      if (Math.abs(diff) < EPSILON) {
        progress = targetProgress;
        render();
        rafId = 0;
        return;
      }
      progress += diff * LERP;
      render();
      rafId = requestAnimationFrame(tick);
    }

    function ensureTicking() {
      if (!rafId) rafId = requestAnimationFrame(tick);
    }

    function init() {
      measure();
      setInitialLogos();
      setProgressInstant(0);
    }

    init();
    let resizeRaf = 0;
    window.addEventListener('resize', () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => { measure(); setProgressInstant(progress); });
    });

    return {
      // Krawędzie liczone na PROGRESS (aktualny), nie target — żeby outer
      // Swiper nie odpalał slideNext zanim animacja dojechała wizualnie.
      get isBeginning() { return progress <= 0 && targetProgress <= 0; },
      get isEnd()       { return progress >= 1 && targetProgress >= 1; },
      addDelta(deltaPx) {
        targetProgress = Math.max(0, Math.min(1, targetProgress + deltaPx / travel));
        ensureTicking();
      },
      reset()  { measure(); setInitialLogos(); setProgressInstant(0); },
      toLast() { measure(); setInitialLogos(); setProgressInstant(1); },
    };
  }

  /* =========================
     WHEEL LOCK
     Globalny handler na window (capture) — wyprzedza Lenis.
     Aktywny gdy container jest przy szczycie viewportu.
  ========================= */
  let wheelCleanup = null;

  function setupWheelHandler(swiper) {
    // Prosty cooldown — pierwszy event po cooldown wykonuje akcję, kolejne
    // ignorowane przez COOLDOWN_MS. Bez wykrywania "ciszy" (trackpad ma długi
    // inertia tail, nie da się rzetelnie wykryć kiedy user oderwał palec).
    const COOLDOWN_MS = 700;
    const MIN_DELTA = 5; // ignoruj mikrodelty z inertia tail trackpada
    let lastActionTime = 0;

    function tryAction(fn) {
      const now = Date.now();
      if (now - lastActionTime < COOLDOWN_MS) return false;
      lastActionTime = now;
      fn();
      return true;
    }

    function onWheel(e) {
      // .horizontal jest position:fixed, więc rect.top to zawsze 0 — nie da się
      // tym wykryć aktywności sekcji. Zamiast tego patrzymy na scrollY względem
      // końca pinu startera (window.__flipTimeline.scrollTrigger.end). Powyżej:
      // user jest na swiperze; poniżej: jeszcze w starterze — uwolnij scroll do Lenis.
      const flipTl = window.__flipTimeline;
      const pinEnd = flipTl && flipTl.scrollTrigger ? flipTl.scrollTrigger.end : 0;
      if (window.scrollY < pinEnd - 10) return;

      // Ignoruj mikrodelty z inertia tail trackpada (mniejsze niż MIN_DELTA).
      if (Math.abs(e.deltaY) < MIN_DELTA) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      const scrollingDown = e.deltaY > 0;
      const scrollingUp   = e.deltaY < 0;

      // Sprawdź czy jesteśmy na panelu offer
      const currentSlide = swiper.slides[swiper.activeIndex];
      const panelName    = currentSlide ? currentSlide.dataset.panel : null;

      if (panelName === 'offer' && offerCycler) {
        if (scrollingUp && offerCycler.isBeginning) {
          if (swiper.isBeginning) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          tryAction(() => swiper.slidePrev());
          return;
        }
        if (scrollingDown && offerCycler.isEnd) {
          e.preventDefault();
          e.stopImmediatePropagation();
          tryAction(() => swiper.slideNext());
          return;
        }
        // Środek oferty → zmień element oferty
        e.preventDefault();
        e.stopImmediatePropagation();
        tryAction(() => (scrollingDown ? offerCycler.next() : offerCycler.prev()));
        return;
      }

      // Trust — scroll-driven marquee logos: ciągły wheel = ciągły translateX.
      // Outer Swiper przechodzi do następnego/poprzedniego dopiero gdy progress
      // dotarł do krawędzi (z małym debouncem żeby nie przeskakiwał natychmiast).
      if (panelName === 'trust' && trustScroller) {
        if (scrollingDown && trustScroller.isEnd) {
          e.preventDefault();
          e.stopImmediatePropagation();
          tryAction(() => swiper.slideNext());
          return;
        }
        if (scrollingUp && trustScroller.isBeginning) {
          e.preventDefault();
          e.stopImmediatePropagation();
          tryAction(() => swiper.slidePrev());
          return;
        }
        // Środek — konsumuj delta na progress logos (bez debounce, ciągły scroll)
        e.preventDefault();
        e.stopImmediatePropagation();
        trustScroller.addDelta(e.deltaY);
        return;
      }

      // Scroll w górę na pierwszym slajdzie → uwolnij (Lenis scrolluje do startera)
      if (swiper.isBeginning && scrollingUp) return;

      // Outer Swiper navigation
      e.preventDefault();
      e.stopImmediatePropagation();
      tryAction(() => (scrollingDown ? swiper.slideNext() : swiper.slidePrev()));
    }

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => window.removeEventListener('wheel', onWheel, { capture: true });
  }

  /* =========================
     MOUNT OUTER SWIPER
  ========================= */
  function mountSwiper() {
    const swiper = new window.Swiper(container, {
      direction: 'horizontal',
      slidesPerView: 1,
      speed: 800,
      effect: 'slide',
      resistanceRatio: 0,
      // Wyłączone: grab&swipe (touch + drag) — nawigacja TYLKO wheel/keyboard.
      simulateTouch: false,
      allowTouchMove: false,
      mousewheel: false,

      keyboard: { enabled: true, onlyInViewport: true },

      on: {
        slideChange(sw) {
          const prevSlide = sw.slides[sw.previousIndex];
          const prevName  = prevSlide ? (prevSlide.dataset.panel || String(sw.previousIndex)) : String(sw.previousIndex);
          dispatch('panel:leave', { index: sw.previousIndex, panelName: prevName });
        },

        slideChangeTransitionStart(sw) {
          const activeSlide = sw.slides[sw.activeIndex];
          const panelName   = activeSlide ? (activeSlide.dataset.panel || String(sw.activeIndex)) : String(sw.activeIndex);

          const goingBack = sw.previousIndex > sw.activeIndex;

          // Resetuj offer cycler do właściwego końca przy wejściu z lewej/prawej
          if (offerCycler && panelName === 'offer') {
            if (goingBack) offerCycler.toLast();
            else           offerCycler.reset();
          }

          // Trust scroller: wejście od lewej → progress 0; od prawej → 1
          if (trustScroller && panelName === 'trust') {
            if (goingBack) trustScroller.toLast();
            else           trustScroller.reset();
          }

          dispatch('panel:enter', { index: sw.activeIndex, panelName });
        },

        slideChangeTransitionEnd(sw) {
          const activeSlide = sw.slides[sw.activeIndex];
          const panelName   = activeSlide ? (activeSlide.dataset.panel || String(sw.activeIndex)) : String(sw.activeIndex);
          dispatch('panel:enter:end', { index: sw.activeIndex, panelName });
        },
      },
    });

    window.__horizontalSwiper = swiper;
    wheelCleanup = setupWheelHandler(swiper);

    return swiper;
  }

  /* =========================
     CLEANUP (mobile / resize)
  ========================= */
  function destroySwiper() {
    if (wheelCleanup) { wheelCleanup(); wheelCleanup = null; }
    if (window.__horizontalSwiper) {
      window.__horizontalSwiper.destroy(true, true);
      window.__horizontalSwiper = null;
    }
    offerCycler = null;
    trustScroller = null;
  }

  function setupCleanup(swiperInstance) {
    if (typeof gsap !== 'undefined' && gsap.matchMedia) {
      gsap.matchMedia().add('(min-width: 1024px)', () => () => destroySwiper());
    } else {
      const mq = window.matchMedia('(min-width: 1024px)');
      const onChange = (e) => {
        if (!e.matches) { destroySwiper(); mq.removeEventListener('change', onChange); }
      };
      mq.addEventListener('change', onChange);
      swiperInstance._mqCleanup = () => mq.removeEventListener('change', onChange);
    }
  }

  /* =========================
     INIT po starter:finished
  ========================= */
  document.addEventListener(
    'starter:finished',
    () => {
      const swiper = mountSwiper();
      setupCleanup(swiper);
      offerCycler = setupOfferCycler();
      trustScroller = setupTrustScroller();
    },
    { once: true },
  );
}
